import React from 'react'

import * as c from '../../../common'
import {
  COMPANY_SELECTOR_TIMEOUT_MS,
  companySelectorReady,
} from '../../../common/helpers/companySelectorReady'
import * as r from '../../../react-utils'

/**
 * Whether the company selectors of this report may open. Default true: a
 * selector outside the report keeps working as it always did.
 */
export const CompanySelectorReadyContext = React.createContext(true)

interface DbValues {
  company: string | undefined
  customerID: string
  failed: boolean
  state: string | undefined
}

/** One read-only listener per report, in `CustomerReport`, for all selectors. */
export const useCompanySelectorLoad = (customerID: string): boolean => {
  const [{ contractCompany, solarCompany }] = r.useCustomer(customerID)
  const companies = r.useEnv<c.ContractCompanies>('contractCompanies')
  const [db, setDb] = React.useState<DbValues>({
    company: undefined,
    customerID,
    failed: false,
    state: undefined,
  })
  const [timedOutID, setTimedOutID] = React.useState('')

  React.useEffect(() => {
    if (!customerID) return
    let active = true
    const unsubscribers: (() => void)[] = []
    const merge = (values: Partial<DbValues>) => {
      if (active)
        setDb((prev) => ({
          ...(prev.customerID === customerID
            ? prev
            : {
                company: undefined,
                customerID,
                failed: false,
                state: undefined,
              }),
          ...values,
        }))
    }
    const fail = () => merge({ failed: true })
    try {
      unsubscribers.push(
        c.subToCustomerField(
          customerID,
          'contractCompany',
          (company) => merge({ company }),
          fail,
        ),
        c.subToCustomerField(
          customerID,
          'solarCompany',
          (state) => merge({ state }),
          fail,
        ),
      )
    } catch {
      fail()
    }
    const timer = setTimeout(() => {
      if (active) setTimedOutID(customerID)
    }, COMPANY_SELECTOR_TIMEOUT_MS)
    return () => {
      active = false
      clearTimeout(timer)
      for (const unsubscribe of unsubscribers) unsubscribe()
    }
  }, [customerID])

  const current = db.customerID === customerID
  return companySelectorReady({
    companiesLoaded: !!companies && Object.keys(companies).length > 0,
    dbCompany: current ? db.company : undefined,
    dbState: current ? db.state : undefined,
    failed: current && db.failed,
    storeCompany: contractCompany,
    storeState: solarCompany,
    timedOut: timedOutID === customerID,
  })
}
